import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { createStage, resizeStage, stepStage, type StageEvent, type StageState, type Vec } from './poseModel'
import { drawStage, type StageLook } from './drawStage'

/**
 * The left half of the staff sign-in and the school enquiry: a picture of the
 * Kalqy world with a child drawn over it the way the camera keeps one —
 * seventeen points — playing a round of the real game. The reader can steer
 * it with the pointer, and "Camera view" takes the picture away to show what
 * is left: the points, and nothing else.
 *
 * 🔴 IT IS A DEMO, NOT A CAMERA. Nothing here asks for the webcam; the figure is
 * simulated (`poseModel`). It shows the promise on the right ("Camera never
 * records") instead of stating it.
 *
 * ⚠️ IT COSTS NOTHING ON A PHONE. The panel only renders from `lg` (its parent
 * hides it below), the loop stops when the panel or the tab is out of sight,
 * the video is mounted only on a wide screen without reduced motion or
 * Save-Data, and with reduced motion the figure moves only while the reader
 * steers it.
 */

export type ShowcaseSceneId = 'signin' | 'contact'

interface SceneMedia {
  poster: string
  video?: { webm: string; mp4: string }
  /** `object-position` for the cover crop — keeps the open sky where the figure plays. */
  position?: string
}

/*
 * ⚠️ NEW ART GETS A NEW FILE NAME. `/landingpage/*` is cached for a week, so
 * art swapped in under the same name would stay stale in browsers that long.
 */
export const SHOWCASE_SCENES: Record<ShowcaseSceneId, SceneMedia> = {
  signin: {
    poster: '/landingpage/auth/signin-meadow.webp',
    video: { webm: '/landingpage/auth/signin-meadow-loop.webm', mp4: '/landingpage/auth/signin-meadow-loop.mp4' },
    position: '50% 50%',
  },
  contact: {
    poster: '/landingpage/auth/contact-sky.webp',
    video: { webm: '/landingpage/auth/contact-sky-loop.webm', mp4: '/landingpage/auth/contact-sky-loop.mp4' },
    position: '58% 50%',
  },
}

/** Wide screen, motion allowed, data not being saved — the conditions for a background video. */
function useVideoAllowed(): boolean {
  const [ok, setOk] = useState(false)
  useEffect(() => {
    const wide = window.matchMedia('(min-width: 1024px)')
    const calm = window.matchMedia('(prefers-reduced-motion: reduce)')
    const saveData = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData === true
    const update = (): void => {
      setOk(wide.matches && !calm.matches && !saveData)
    }
    update()
    wide.addEventListener('change', update)
    calm.addEventListener('change', update)
    return () => {
      wide.removeEventListener('change', update)
      calm.removeEventListener('change', update)
    }
  }, [])
  return ok
}

function promptFor(goal: string): string {
  return /^\d$/.test(goal) ? `Touch the number ${goal}` : `Touch the letter ${goal}`
}

export function ShowcasePanel({ scene, children }: { scene: ShowcaseSceneId; children: ReactNode }) {
  const media = SHOWCASE_SCENES[scene]
  const videoOk = useVideoAllowed()
  const [look, setLook] = useState<StageLook>('scene')
  const [goal, setGoal] = useState('')
  const [score, setScore] = useState(0)
  const [steered, setSteered] = useState(false)
  const [coarse, setCoarse] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const lookRef = useRef<StageLook>('scene')
  const repaint = useRef<() => void>(() => undefined)
  const camera = look === 'camera'

  useEffect(() => {
    lookRef.current = look
    repaint.current()
  }, [look])

  useEffect(() => {
    setCoarse(window.matchMedia('(pointer: coarse)').matches)
  }, [])

  useEffect(() => {
    const root = rootRef.current
    const box = stageRef.current
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!root || !box || !canvas || !ctx) return

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const rand = Math.random
    let state: StageState | null = null
    let dpr = 1
    let pointer: Vec | null = null
    let releaseAt = 0
    let raf = 0
    let last = 0
    let inView = true
    let steeredOnce = false

    const paint = (): void => {
      if (!state) return
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      drawStage(ctx, state, lookRef.current)
    }
    repaint.current = paint

    const fit = (): void => {
      const r = box.getBoundingClientRect()
      const w = Math.max(1, Math.round(r.width))
      const h = Math.max(1, Math.round(r.height))
      dpr = Math.min(2, window.devicePixelRatio || 1)
      canvas.width = Math.round(w * dpr)
      canvas.height = Math.round(h * dpr)
      canvas.style.width = `${String(w)}px`
      canvas.style.height = `${String(h)}px`
      if (state) resizeStage(state, w, h)
      else {
        state = createStage(w, h, rand)
        setGoal(state.goal)
      }
      paint()
    }

    const running = (): boolean => inView && document.visibilityState === 'visible' && (!reduce || pointer !== null)

    /*
     * ADR-022: kept out of the frame body. `stepStage` returns an event only
     * when a round starts or a bubble pops, so this re-renders once per event,
     * never once per frame.
     */
    const announce = (events: StageEvent[], s: StageState): void => {
      for (const e of events) {
        if (e.kind === 'round') setGoal(e.goal)
        else if (e.kind === 'pop') setScore(s.score)
      }
    }

    const frame = (now: number): void => {
      raf = 0
      if (!state) return
      const dt = last ? Math.min(0.05, (now - last) / 1000) : 1 / 60
      last = now
      if (releaseAt && now > releaseAt) {
        pointer = null
        releaseAt = 0
      }
      const s = state
      const events = stepStage(s, dt, { pointer }, rand)
      if (events.length) announce(events, s)
      paint()
      schedule()
    }

    function schedule(): void {
      if (running()) {
        if (!raf) raf = requestAnimationFrame(frame)
      } else {
        last = 0
      }
    }

    /* The whole panel steers; the point is mapped into the stage and held inside it. */
    const toStage = (e: PointerEvent): Vec | null => {
      const r = box.getBoundingClientRect()
      const x = e.clientX - r.left
      const y = e.clientY - r.top
      if (x < -24 || y < -48 || x > r.width + 24 || y > r.height + 24) return null
      return { x: Math.min(r.width, Math.max(0, x)), y: Math.min(r.height, Math.max(0, y)) }
    }
    const onMove = (e: PointerEvent): void => {
      pointer = toStage(e)
      releaseAt = e.pointerType === 'mouse' ? 0 : performance.now() + 1500
      if (pointer && !steeredOnce) {
        steeredOnce = true
        setSteered(true)
      }
      schedule()
    }
    const onLeave = (e: PointerEvent): void => {
      if (e.pointerType === 'mouse') pointer = null
      schedule()
    }
    root.addEventListener('pointermove', onMove)
    root.addEventListener('pointerdown', onMove)
    root.addEventListener('pointerleave', onLeave)

    const resize = new ResizeObserver(fit)
    resize.observe(box)
    const seen = new IntersectionObserver((entries) => {
      inView = entries.some((en) => en.isIntersecting)
      schedule()
    })
    seen.observe(root)
    const onVisibility = (): void => {
      schedule()
    }
    document.addEventListener('visibilitychange', onVisibility)

    fit()
    schedule()

    return () => {
      if (raf) cancelAnimationFrame(raf)
      resize.disconnect()
      seen.disconnect()
      document.removeEventListener('visibilitychange', onVisibility)
      root.removeEventListener('pointermove', onMove)
      root.removeEventListener('pointerdown', onMove)
      root.removeEventListener('pointerleave', onLeave)
      repaint.current = () => undefined
    }
  }, [])

  return (
    <div ref={rootRef} className="relative isolate h-full w-full overflow-hidden rounded-[22px] bg-[#0e1726]">
      {/* ── The picture: the Kalqy world, still or moving ── */}
      <div
        aria-hidden="true"
        className={`absolute inset-0 transition-[filter,transform] duration-700 ease-out ${
          camera ? 'scale-[1.06] blur-[22px] brightness-[0.45] saturate-[0.5]' : ''
        }`}
      >
        <img
          src={media.poster}
          alt=""
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
          style={{ objectPosition: media.position }}
        />
        {videoOk && media.video ? (
          <video
            className="absolute inset-0 h-full w-full object-cover"
            style={{ objectPosition: media.position }}
            autoPlay
            muted
            loop
            playsInline
            poster={media.poster}
          >
            <source src={media.video.webm} type="video/webm" />
            <source src={media.video.mp4} type="video/mp4" />
          </video>
        ) : null}
      </div>

      {/* ── Camera view: the picture gone, a measuring grid in its place ── */}
      <div
        aria-hidden="true"
        className={`absolute inset-0 transition-opacity duration-500 ${camera ? 'opacity-100' : 'opacity-0'}`}
        style={{
          backgroundColor: 'rgba(8, 10, 16, 0.7)',
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.14) 1px, transparent 1.2px)',
          backgroundSize: '22px 22px',
        }}
      />

      {/* ── Scrims: a little at the top for the logo, more at the bottom for the copy ── */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            'linear-gradient(to bottom, rgba(6,10,20,0.38) 0%, rgba(6,10,20,0) 16%), linear-gradient(to top, rgba(6,10,20,0.9) 0%, rgba(6,10,20,0.62) 22%, rgba(6,10,20,0) 48%)',
        }}
      />

      <div className="relative flex h-full flex-col">
        {/* ── Top: the logo and the view switch ── */}
        <div className="flex items-start justify-between gap-4 px-7 pt-6">
          <a
            href="#/"
            aria-label="Kalqy home"
            className="inline-flex items-center rounded-full bg-white/92 px-3.5 py-2 shadow-[0_6px_20px_rgba(6,10,20,0.18)] backdrop-blur-md transition-transform duration-150 hover:-translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
          >
            <img src="/images/kalqy-brand-logo.png" alt="Kalqy" width={896} height={264} className="h-6 w-auto" />
          </a>

          <div
            role="group"
            aria-label="Demo view"
            className="flex rounded-full bg-black/30 p-1 ring-1 ring-white/20 backdrop-blur-md"
          >
            {(
              [
                ['scene', 'Scene'],
                ['camera', 'Camera view'],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                aria-pressed={look === key}
                data-testid={`showcase-look-${key}`}
                onClick={() => {
                  setLook(key)
                }}
                className={`inline-flex h-7 items-center gap-1.5 rounded-full px-3 text-[12px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white ${
                  look === key ? 'bg-white text-[#141413]' : 'text-white/85 hover:text-white'
                }`}
              >
                {key === 'camera' ? <DotsIcon /> : <SceneIcon />}
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* ── The stage ── */}
        <div ref={stageRef} className="relative mx-4 mt-2 min-h-0 flex-1 cursor-crosshair touch-none select-none">
          <canvas ref={canvasRef} aria-hidden="true" className="absolute inset-0" />
          <p className="sr-only">
            A demo of a Kalqy game: a child drawn as seventeen points reaches for the bubble the game asks for. Those
            points are all Kalqy&rsquo;s camera keeps.
          </p>

          {goal ? (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute top-2 left-1/2 flex -translate-x-1/2 items-center gap-2.5 rounded-full bg-white/95 py-1.5 pr-1.5 pl-1.5 shadow-[0_10px_30px_rgba(6,10,20,0.22)]"
            >
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#e5007a] text-white">
                <MicIcon />
              </span>
              <span className="text-[13px] font-semibold whitespace-nowrap text-[#141413]">{promptFor(goal)}</span>
              <span className="inline-flex h-7 items-center gap-1 rounded-full bg-[#f3f2ee] px-2.5 text-[12px] font-semibold text-[#141413] tabular-nums">
                <StarIcon />
                {score}
              </span>
            </div>
          ) : null}

          <div
            aria-hidden="true"
            className={`pointer-events-none absolute top-[54px] left-1/2 -translate-x-1/2 rounded-full bg-black/40 px-3 py-1 text-[11.5px] font-medium whitespace-nowrap text-white/90 ring-1 ring-white/15 backdrop-blur-md transition-opacity duration-500 ${
              camera || steered ? 'opacity-0' : 'opacity-100'
            }`}
          >
            {coarse ? 'Drag here to play' : 'Move your cursor here to play'}
          </div>

          <div
            aria-hidden="true"
            className={`pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/55 px-3.5 py-1.5 text-[12px] font-medium whitespace-nowrap text-white ring-1 ring-white/15 backdrop-blur-md transition-opacity duration-500 ${
              camera ? 'opacity-100' : 'opacity-0'
            }`}
          >
            17 points · no photo, no video · nothing leaves the laptop
          </div>
        </div>

        {/* ── The page's own words ── */}
        <div className="px-10 pt-5 pb-9 text-white">{children}</div>
      </div>
    </div>
  )
}

function MicIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
    </svg>
  )
}

function StarIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="#f5b301" aria-hidden="true">
      <path d="M12 2.5l2.9 6.1 6.6.8-4.9 4.6 1.3 6.6L12 17.3l-5.9 3.3 1.3-6.6L2.5 9.4l6.6-.8z" />
    </svg>
  )
}

function SceneIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="16" rx="3" />
      <path d="M3 16l5-5 4 4 3-3 6 6" />
    </svg>
  )
}

function DotsIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="12" cy="5" r="2" />
      <circle cx="7" cy="11" r="2" />
      <circle cx="17" cy="11" r="2" />
      <circle cx="9" cy="19" r="2" />
      <circle cx="15" cy="19" r="2" />
    </svg>
  )
}
